import React, { useCallback, useMemo, useRef } from 'react';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalHeader,
  UncontrolledDropdown,
} from 'reactstrap';
import classNames from 'classnames';
import { Icon } from 'js/components';
import { getQuestionMeta } from 'js/utils/questions';
import {
  isEligibleBreakoutQuestion,
  ELIGIBLE_QUESTION_TYPES,
} from 'js/utils/assessment-cohorts';
import { getQuestionSetCategory } from 'js/utils/question-set';
import styles from './BreakoutQuestionPickerModal.module.scss';

function buildQuestionSets(assessment) {
  return (assessment?.question_sets ?? [])
    .map((qs) => ({
      id: qs.id,
      key: qs.key,
      name: qs.name,
      category: getQuestionSetCategory(qs),
      questions: (qs.questions ?? []).filter(isEligibleBreakoutQuestion),
    }))
    .filter((qs) => qs.questions.length > 0);
}

function categoryPaletteClass(category, categoryColorMap) {
  return categoryColorMap?.[category] ?? styles['palette-nil'];
}

function QuestionSetCategoryBar({ category, categoryColorMap }) {
  return (
    <span
      className={classNames(
        styles.categoryBar,
        categoryPaletteClass(category, categoryColorMap)
      )}
      aria-hidden="true"
    />
  );
}

export default function BreakoutQuestionPickerModal({
  isOpen,
  assessment,
  categoryColorMap,
  selectedQuestionId = null,
  onClose,
  onSelect,
}) {
  const questionSets = useMemo(
    () => buildQuestionSets(assessment),
    [assessment]
  );
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});

  const scrollToSet = useCallback((setId) => {
    const el = sectionRefs.current[setId] || sectionRefs.current[String(setId)];
    const container = scrollRef.current;
    if (!el || !container) {
      return;
    }
    container.scrollTo({
      top: el.offsetTop - container.offsetTop,
      behavior: 'smooth',
    });
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen
      size="xl"
      className={styles.modalRoot}
      centered
      toggle={onClose}
    >
      <ModalHeader toggle={onClose}>Choose a question</ModalHeader>
      <ModalBody>
        <div className={styles.layout}>
          <nav className={styles.sidebar} aria-label="Question sets">
            <div className={styles.sidebarTitle}>Question sets</div>
            <ul className={styles.sidebarList}>
              {questionSets.map((qs) => (
                <li key={qs.id}>
                  <button
                    type="button"
                    className={classNames(
                      'btn-card',
                      'btn-card-secondary',
                      'btn-card-borderless',
                      styles.sidebarLink
                    )}
                    onClick={(e) => {
                      scrollToSet(qs.id);
                      e.currentTarget.blur();
                    }}
                  >
                    <QuestionSetCategoryBar
                      category={qs.category}
                      categoryColorMap={categoryColorMap}
                    />
                    <span>{qs.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.mobileJump}>
            <UncontrolledDropdown className={styles.mobileJumpDropdown}>
              <DropdownToggle
                caret
                color="secondary"
                outline
                size="sm"
                className={styles.jumpToggle}
              >
                Jump to
              </DropdownToggle>
              <DropdownMenu className={styles.jumpMenu}>
                {questionSets.map((qs) => (
                  <DropdownItem
                    key={qs.id}
                    className={classNames(
                      'btn-card',
                      'btn-card-secondary',
                      'btn-card-borderless',
                      styles.sidebarLink,
                      styles.jumpItem
                    )}
                    onClick={() => scrollToSet(qs.id)}
                  >
                    <QuestionSetCategoryBar
                      category={qs.category}
                      categoryColorMap={categoryColorMap}
                    />
                    <span>{qs.name}</span>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>

          <div className={styles.scrollArea} ref={scrollRef}>
            {questionSets.length === 0 ? (
              <p className="text-muted">
                No eligible questions. Breakouts support{' '}
                {ELIGIBLE_QUESTION_TYPES.join(', ')}.
              </p>
            ) : (
              questionSets.map((qs) => (
                <section
                  key={qs.id}
                  className={styles.questionSet}
                  ref={(el) => {
                    sectionRefs.current[qs.id] = el;
                    sectionRefs.current[String(qs.id)] = el;
                  }}
                >
                  <h3 className={styles.questionSetTitle}>
                    {qs.name}
                    <span
                      className={classNames(
                        styles.categoryUnderline,
                        categoryPaletteClass(qs.category, categoryColorMap)
                      )}
                      aria-hidden="true"
                    />
                  </h3>
                  <ul className={styles.questionList}>
                    {qs.questions.map((question) => {
                      const typeMeta = getQuestionMeta(question.type);
                      const selected =
                        Number(selectedQuestionId) === Number(question.id);
                      const answers = (question.answers || []).filter(
                        (a) => !a.is_na
                      );
                      return (
                        <li key={question.id}>
                          <button
                            type="button"
                            className={classNames(
                              'btn-card',
                              'btn-card-info',
                              styles.questionCard,
                              { active: selected }
                            )}
                            onClick={() =>
                              onSelect?.({
                                ...question,
                                questionSet: {
                                  id: qs.id,
                                  key: qs.key,
                                  name: qs.name,
                                },
                                question_set_key: qs.key,
                              })
                            }
                          >
                            <div className={styles.questionMeta}>
                              {qs.key} / {question.key}
                              {typeMeta.name ? (
                                <>
                                  {' · '}
                                  <Icon
                                    icon={typeMeta.icon}
                                    className={styles.typeIcon}
                                  />{' '}
                                  {typeMeta.name}
                                </>
                              ) : null}
                            </div>
                            <div className={styles.questionText}>
                              {question.text}
                            </div>
                            {answers.length > 0 ? (
                              <ul className={styles.answerList}>
                                {answers.map((answer) => (
                                  <li key={answer.id}>{answer.text}</li>
                                ))}
                              </ul>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
